import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
// Core
import { Grid, Box } from '@mui/material';
// Components
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Button, Checkbox } from 'Components';
import MultipleSubModules from './multipleSubModules';
import SingleSubModule from './singleSubModule';
// Utilities
import { getFeatureDisplayCode } from '../modules/hierarchicalFeatures';
import {
  isSingleSelectionModule,
  applyDefaultSingleSelection,
  normalizeSingleSelection,
  selectSingleSubModule,
} from '../modules/singleSelectionModules';
//Styles
import './styles.scss';

// groupNames whose lone "Edit" subModule is hidden
const GROUPS_WITH_HIDDEN_LONE_EDIT = [
  'Contacts',
  'API',
  'Roles',
  'Tags',
  'Users',
  'Asset',
  'AssetSubtypes',
  'KeyFobs',
  'Temperature',
  'Crews',
  'Driver',
  'Fuelcards',
  'Inputs',
  'Locations',
  'Equipment',
  'VehicleStatus',
  'Services',
  'UnitSettings',
  'DriverStatus',
  'Behavior',
  'WorkOrdersCatalogs',
];

/**
 * SubModules checked state (multi-selection modules) when global access is toggled
 */
const subModulesForGlobalToggle = (subModules, checked) =>
  subModules.map((subModule) => ({
    ...subModule,
    // When enabling: only auto-check truly independent features (no :: at all);
    // features with ::parent or ::! stay unchecked. When disabling: uncheck all.
    checked: checked && !subModule.code.includes('::'),
  }));

const SubModules = (props) => {
  const { messages } = useIntl();

  //Props
  const {
    onShow,
    handleClose,
    module
  } = props;

  //State local
  const [sections, setSections] = useState();

  const singleSelection = isSingleSelectionModule(module);

  const initialValues = () => {
    let result;
    if (module) {
      result = {
        globalPermission: module,
        localPermissions: []
      }
      let resultSubModules = module.subModules || [];
      resultSubModules = resultSubModules.filter(subModule =>
        !GROUPS_WITH_HIDDEN_LONE_EDIT.includes(subModule.groupName) ||
        subModule.name !== 'Edit' ||
        resultSubModules.some(item =>
          item.groupName === subModule.groupName &&
          item.name !== 'Edit'
        )
      );

      // Single-selection modules must always have exactly one checked subModule
      if (singleSelection) {
        resultSubModules = normalizeSingleSelection({ ...module, subModules: resultSubModules });
      }

      //Filtrar sub modulos por secciones
      const filterGroupName = resultSubModules.map(subModule => {
        return [subModule.groupName, subModule]
      });

      const sections = [...new Map(filterGroupName).values()];

      sections.forEach((section) => {
        result.localPermissions.push({
          groupName: section.groupName,
          subModules: resultSubModules.filter((subModule) => subModule.groupName === section.groupName)
        });
      });

      setSections(result);
    } else {
      setSections();
    }
  }

  const closeModal = () => {
    if (sections) {
      let moduleResult = {
        ...sections.globalPermission,
        subModules: []
      }
      sections.localPermissions.forEach((item) => {
        item.subModules.forEach(subModule => {
          moduleResult.subModules.push(subModule)
        });
      })
      handleClose(moduleResult);
    } else {
      handleClose(module);
    }
  }

  const handleGlobalChange = (checked) => {
    const globalPermission = { ...sections.globalPermission, checked };
    let localPermissions;

    if (singleSelection) {
      // Exactly one subModule across every group: the module default (e.g. coachsso_admin),
      // resolved over the flat list so a group without it does not pick its own first item
      const allSubModules = sections.localPermissions.flatMap((item) => item.subModules);
      const target = checked
        ? applyDefaultSingleSelection({ ...globalPermission, subModules: allSubModules }).find((s) => s.checked)
        : null;
      const targetCode = target ? getFeatureDisplayCode(target.code) : null;
      localPermissions = sections.localPermissions.map((item) => ({
        ...item,
        subModules: selectSingleSubModule(item.subModules, targetCode),
      }));
    } else {
      localPermissions = sections.localPermissions.map((item) => ({
        ...item,
        subModules: subModulesForGlobalToggle(item.subModules, checked),
      }));
    }

    setSections({ ...sections, globalPermission, localPermissions });
  };

  const handleLocalChange = (localPermissions) => {
    setSections({ ...sections, localPermissions });
  };

  useEffect(() => {
    if (module) {
      initialValues();
    }
  }, [module])

  const LocalPermissions = singleSelection ? SingleSubModule : MultipleSubModules;

  return (
    <Modal
      title={messages[`role_${module?.code}`] || module?.name}
      open={onShow}
      size="small"
      handleClose={() => closeModal()}
      position="right"
    >
      <ModalBody className={'modalBodyComplete'}>
        <div style={{ padding: 24, paddingBottom: 40 }}>
          {
            sections &&
            <Grid
              container
              justifyContent={'center'}
              direction={'column'}
              className="paddingBody"
              spacing={2}
            >
              <Grid item sm={12}>
                <label className="title-permission">{messages['role_globalAccess']}</label>
                <Grid key={`global-permissions`} item sm={12}>
                  {
                    sections.globalPermission.userCode !== 0 ? // Module Map
                      <Checkbox
                        value={sections.globalPermission.checked}
                        onChange={handleGlobalChange}
                        size="small"
                        id={`permission-${sections.globalPermission.code}`}
                        label={messages[`role_${sections.globalPermission.code}`]}
                      /> :
                      <Box mt={1} mb={1}><label>{messages[`role_${sections.globalPermission.code}`]}</label></Box>
                  }
                </Grid>
              </Grid>
              {sections.localPermissions.length ?
                <LocalPermissions
                  localPermissions={sections.localPermissions}
                  globalPermission={sections.globalPermission}
                  onChange={handleLocalChange}
                />
                : ''}
            </Grid>
          }
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => closeModal()}
          className="btn-cancel"
        >
          {messages["permissions_back"]}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SubModules;
